import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Pagination, Badge } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { companyService } from '../services/apiService';
import { Check, X } from 'lucide-react';
import PlanFilter, { planKey, planOptions } from '../components/PlanFilter';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useAuthToken } from '../hooks/useAuthToken';
import StackedTable from '../components/StackedTable';
import SortableHeader from '../components/SortableHeader';
import { LoadingState, ErrorState } from '../components/PageState';
import { formatNumber, plural } from '../utils/format';
import { rowClick } from '../utils/rows';

/** The reports index: every report under the question it answers, each with one line on what it shows. */
const REPORT_GROUPS = [
  {
    title: 'Growth',
    reports: [
      { to: '/user-signups', title: 'User signups', desc: 'Users and companies created each month, and the latest sign-ups.' },
      { to: '/reports/signup-conversion', title: 'Signup conversion', desc: 'What share of new companies go on to pay, and how quickly.' },
      { to: '/reports/company-admins', title: 'Company admins', desc: 'Every company admin, filtered by when the company was created, paid seats and plan.' },
    ],
  },
  {
    title: 'Usage',
    reports: [
      { to: '/reports/engagement', title: 'Engagement', desc: 'Whether Team Today is used more or less over time, overall and by company.' },
    ],
  },
  {
    title: 'Data quality',
    reports: [
      { to: '/reports/shared-email-domains', title: 'Shared email domains', desc: 'Email domains split across companies, usually a duplicate account.' },
      { to: '/unregistered-user-visits', title: 'Unregistered user visits', desc: 'People who opened Team Today in Teams without an account.' },
    ],
  },
  {
    title: 'System',
    reports: [
      { to: '/system-report', title: 'Whereabouts by type', desc: 'Home, office and other days each week, across every company.' },
    ],
  },
  {
    title: 'Marketing',
    reports: [
      { to: '/data-insights', title: 'Data insights', desc: 'Anonymised stats across all customers, ready for social posts.' },
    ],
  },
];

const ReportIndex = () => (
  <Row className="g-3 g-md-4 mb-5">
    {REPORT_GROUPS.map((group) => {
      const headingId = `reports-${group.title.toLowerCase().replace(/\s+/g, '-')}`;
      return (
        <Col key={group.title} xs={12} md={6} xl>
          <section aria-labelledby={headingId}>
            <h2 id={headingId} className="h6 text-muted mb-2">{group.title}</h2>
            <div className="d-grid gap-3">
              {group.reports.map((r) => (
                <Card key={r.to} as={Link} to={r.to} className="shadow-sm text-decoration-none text-reset">
                  <Card.Body>
                    <div className="fw-semibold mb-1">{r.title}</div>
                    <div className="small text-muted">{r.desc}</div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </section>
        </Col>
      );
    })}
  </Row>
);

const Reports = () => {
  const refreshToken = useAuthToken();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterEnabled, setFilterEnabled] = useState('all');
  const [filterPaid, setFilterPaid] = useState('all');
  const [hiddenPlans, setHiddenPlans] = useState(() => new Set());
  const [sortField, setSortField] = useState('totalUsers');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();
  const pageSize = 25;

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      await refreshToken();
      // totalUsers comes from the backend as the live headcount from the users table;
      // freeUsers/paidUsers are the seat allowance/licence fields.
      const response = await companyService.getAllCompanies();
      setCompanies(response.data);
    } catch (err) {
      setError('Could not load the companies.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  // Paid means paid seats, the same rule as the Paid companies tile. These filters, and the search,
  // also scope the stat tiles, so the tiles always describe the companies in the table.
  const matchesFilters = (c) => {
    const q = search.trim().toLowerCase();
    if (q && !(c.name || '').toLowerCase().includes(q)) return false;
    if (filterEnabled === 'enabled' && !c.enabled) return false;
    if (filterEnabled === 'disabled' && c.enabled) return false;
    if (filterPaid === 'paid' && !(c.paidUsers > 0)) return false;
    if (filterPaid === 'unpaid' && c.paidUsers > 0) return false;
    return !hiddenPlans.has(planKey(c));
  };

  const getFilteredCompanies = () => companies.filter(matchesFilters);

  const getSortedAndFilteredCompanies = () => {
    const filtered = getFilteredCompanies();

    const sorted = [...filtered].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }

      if (typeof aValue === 'boolean') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const getTotalStats = () => {
    const filtered = getFilteredCompanies();
    return {
      paidCompanies: filtered.filter(c => c.paidUsers > 0).length,
      totalUsers: filtered.reduce((sum, c) => sum + (c.totalUsers || 0), 0),
      // Registered users not covered by a paid seat
      usersWithoutPaidSeat: filtered.reduce((sum, c) => sum + Math.max(0, (c.totalUsers || 0) - (c.paidUsers || 0)), 0),
      paidSeats: filtered.reduce((sum, c) => sum + (c.paidUsers || 0), 0),
    };
  };

  const stats = getTotalStats();
  const filteredCompanies = getSortedAndFilteredCompanies();
  const totalPages = Math.ceil(filteredCompanies.length / pageSize);
  const paginatedCompanies = filteredCompanies.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const sortProps = { sortField, sortDirection, onSort: handleSort };
  const selectStyle = { width: isMobile ? '100%' : 'auto' };

  const tile = (label, value, primary = false) => (
    <Col xs={6} md={3}>
      <Card className="shadow-sm h-100 stat-tile">
        <Card.Body className="text-center">
          <h6 className="text-muted mb-2">{label}</h6>
          <h2 className={primary ? 'text-primary mb-0' : 'mb-0'}>{formatNumber(value)}</h2>
        </Card.Body>
      </Card>
    </Col>
  );

  let companySection;
  if (error) {
    companySection = <Card className="shadow-sm"><ErrorState inCard message={error} onRetry={fetchCompanies} /></Card>;
  } else if (loading) {
    companySection = <Card className="shadow-sm"><LoadingState label="Loading companies" /></Card>;
  } else {
    companySection = (
      <>
        <Row className="g-3 g-md-4 mb-4">
          {tile('Paid companies', stats.paidCompanies, true)}
          {tile('Total users', stats.totalUsers)}
          {tile('Users without a paid seat', stats.usersWithoutPaidSeat)}
          {tile('Paid seats', stats.paidSeats)}
        </Row>

        <Card className="shadow-sm">
          <Card.Header className="bg-light d-flex align-items-center flex-wrap gap-2 gap-md-3 filter-bar">
            <Form.Control
              size="sm"
              type="search"
              placeholder="Search companies"
              aria-label="Search companies"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="filter-bar-wide"
              style={{ maxWidth: 280 }}
            />
            <Form.Group controlId="reportsStatus" className="d-flex align-items-center gap-2 ms-md-auto">
              <Form.Label className="small text-muted mb-0">Status</Form.Label>
              <Form.Select size="sm" value={filterEnabled} onChange={(e) => { setFilterEnabled(e.target.value); setCurrentPage(1); }} style={selectStyle} className={isMobile ? undefined : 'pe-5'}>
                <option value="all">All</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="reportsPaidSeats" className="d-flex align-items-center gap-2">
              <Form.Label className="small text-muted mb-0 text-nowrap">Paid seats</Form.Label>
              <Form.Select size="sm" value={filterPaid} onChange={(e) => { setFilterPaid(e.target.value); setCurrentPage(1); }} style={selectStyle} className={isMobile ? undefined : 'pe-5'}>
                <option value="all">Any</option>
                <option value="paid">Some</option>
                <option value="unpaid">None</option>
              </Form.Select>
            </Form.Group>
            <PlanFilter
              id="reportsPlans"
              options={planOptions(companies)}
              hidden={hiddenPlans}
              onChange={(next) => { setHiddenPlans(next); setCurrentPage(1); }}
            />
          </Card.Header>
          <Card.Body className="p-0">
            {filteredCompanies.length === 0 ? (
              <div className="p-4 text-center text-muted">No companies match</div>
            ) : (
              /* On phones each company is a card of its headcounts; the feature columns are desktop detail */
              <StackedTable responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <SortableHeader field="name" {...sortProps}>Company</SortableHeader>
                    <SortableHeader field="freeUsers" align="text-end d-none d-md-table-cell" {...sortProps}>Free seats</SortableHeader>
                    <SortableHeader field="paidUsers" align="text-end" {...sortProps}>Paid seats</SortableHeader>
                    <SortableHeader field="totalUsers" align="text-end" {...sortProps}>Total users</SortableHeader>
                    <SortableHeader field="numberOfTeamsLimit" align="text-end d-none d-md-table-cell" {...sortProps}>Teams limit</SortableHeader>
                    <SortableHeader field="enableHolidayBooking" align="text-center d-none d-md-table-cell" {...sortProps}>Holiday booking</SortableHeader>
                    <SortableHeader field="deskCount" align="text-center d-none d-md-table-cell" {...sortProps}>Desk booking</SortableHeader>
                    <SortableHeader field="lineManagementEnabled" align="text-center d-none d-md-table-cell" {...sortProps}>Line management</SortableHeader>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCompanies.map((company) => (
                    <tr
                      key={company.id}
                      onClick={rowClick(() => navigate(`/companies/${company.id}`))}
                      style={{ cursor: 'pointer' }}
                      className="table-row-hover"
                    >
                      <td className="align-middle">
                        <Link className="cell-link" to={`/companies/${company.id}`}>{company.name}</Link>
                        {!company.enabled && <Badge bg="secondary" className="ms-2">Disabled</Badge>}
                      </td>
                      <td className="text-end align-middle d-none d-md-table-cell">{formatNumber(company.freeUsers)}</td>
                      <td className="text-end align-middle">{formatNumber(company.paidUsers)}</td>
                      <td className="text-end align-middle">
                        <strong>{formatNumber(company.totalUsers)}</strong>
                      </td>
                      <td className="text-end align-middle d-none d-md-table-cell">{formatNumber(company.numberOfTeamsLimit)}</td>
                      <td className="text-center align-middle d-none d-md-table-cell">
                        {company.enableHolidayBooking ? <Check size={16} className="text-success" aria-label="Yes" /> : <X size={16} className="text-muted" aria-label="No" />}
                      </td>
                      <td className="text-center align-middle d-none d-md-table-cell">
                        {/* Ticked when the company has set up at least one desk */}
                        <span title={company.deskCount > 0 ? plural(company.deskCount, 'desk') : 'No desks'}>
                          {company.deskCount > 0
                            ? <Check size={16} className="text-success" aria-label={plural(company.deskCount, 'desk')} />
                            : <X size={16} className="text-muted" aria-label="No desks" />}
                        </span>
                      </td>
                      <td className="text-center align-middle d-none d-md-table-cell">
                        {company.lineManagementEnabled ? <Check size={16} className="text-success" aria-label="Yes" /> : <X size={16} className="text-muted" aria-label="No" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </StackedTable>
            )}
          </Card.Body>
          {totalPages > 1 && (
            <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <small className="text-muted">
                Showing {formatNumber((currentPage - 1) * pageSize + 1)} to {formatNumber(Math.min(currentPage * pageSize, filteredCompanies.length))} of {plural(filteredCompanies.length, 'company', 'companies')}
              </small>
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                />
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2)
                  .reduce((items, page, idx, arr) => {
                    if (idx > 0 && page - arr[idx - 1] > 1) {
                      items.push(<Pagination.Ellipsis key={`ellipsis-${page}`} disabled />);
                    }
                    items.push(
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                    return items;
                  }, [])}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                />
              </Pagination>
            </Card.Footer>
          )}
        </Card>
      </>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="mb-4">
        <h1>Reports</h1>
        <p className="text-muted mb-0">Every report in one place, then a breakdown of each company's seats and features.</p>
      </div>

      <ReportIndex />

      <h2 className="h4 mb-1">Company breakdown</h2>
      <p className="text-muted small mb-3">Seats, headcount and features for every company. The tiles follow the search and filters.</p>
      {companySection}
    </Container>
  );
};

export default Reports;
